import { getFramework } from '@/framework';
import { createCanvas } from '@/utils/file-kit';
import { getLayerOutputDraft } from '@/utils/layer-output-draft-kit';
import type { IImageLayerProps, ILayerProps, ISize } from '@/version-types';
import { type EdgelessImageLayer } from '../edgeless-layer';
import { getImageLayerEffectPosition, renderImageLayerCanvas } from './render-to-canvas';

export const getImageLayerCanvas = async (
    layer: ILayerProps,
    identity: string,
    options: {
        noEffect?: boolean;
        globalAlpha?: boolean;
        noNeedBorderRadius?: boolean;
        useLayerOpacity?: boolean;
    } = {},
) => {
    const outputDraft = getLayerOutputDraft(layer);
    if (outputDraft?.projected) {
        return renderImageLayerCanvas(layer, {
            ...options,
            baseImage: outputDraft.sourceCanvas,
            effectUnderlayCanvas: outputDraft.effectCanvas,
            effectCachePolicy: outputDraft.usePersistedImageEffect ? 'reuse' : 'preview-rebuild',
            usePersistedEffectOnly: outputDraft.usePersistedImageEffect,
            onEffectArtifact: ({ effectPosition }) => {
                const effect = (layer.data as IImageLayerProps).effect;
                if (effect) effect.effectPosition = effectPosition;
            },
        });
    }

    const object = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id) as
        | EdgelessImageLayer
        | undefined;

    if (!object) {
        throw new Error(`Edgeless image object not found: ${layer.id}`);
    }

    const canvas = await object.getCanvasResult(options);

    if (!canvas) {
        throw new Error(`Edgeless image result not found: ${layer.id}`);
    }

    return canvas;
};

export const renderLayerToStage = async (
    layer: ILayerProps,
    resultSize: ISize,
    identity: string,
    includeEffect = true,
): Promise<HTMLCanvasElement | undefined> => {
    try {
        const { left, top, opacity, rotate } = layer;
        const { width: stageWidth, height: stageHeight } = resultSize;
        const layerCanvas = await getImageLayerCanvas(layer, identity, {
            noEffect: !includeEffect,
            globalAlpha: false,
        });

        const centerPoint = {
            x: layer.width / 2 + left,
            y: layer.height / 2 + top,
        };
        const effectPosition = includeEffect
            ? (getLayerOutputDraft(layer)?.effectPosition ?? getImageLayerEffectPosition(layer))
            : undefined;
        const drawOffsetPoint = effectPosition
            ? {
                  x: effectPosition.left - layer.width / 2,
                  y: effectPosition.top - layer.height / 2,
              }
            : {
                  x: -layerCanvas.width / 2,
                  y: -layerCanvas.height / 2,
              };
        const { canvas: destCanvas, ctx: destCtx } = createCanvas(stageWidth, stageHeight);
        destCtx.translate(centerPoint.x, centerPoint.y);
        destCtx.rotate((Math.PI / 180) * rotate);
        destCtx.globalAlpha = opacity;
        destCtx.drawImage(layerCanvas, drawOffsetPoint.x, drawOffsetPoint.y);
        return destCanvas;
    } catch (_error) {
        return undefined;
    }
};
